import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Alert,
  Chip,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useGetTeacherSubjectsQuery } from "../../../../../../app/questionApiSlice";
import { useGetClassroomAssessmentsQuery } from "../../../../../../app/quizApiSlice";
import { useRevertQuizPostMutation } from "../../../../quick-entry/recordApiSlice";

const RevertPostingModal = ({ open, onClose, quiz }) => {
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [error, setError] = useState(null);
  const [reverted, setReverted] = useState(false);

  const { data: teacherSubjectsData = [] } = useGetTeacherSubjectsQuery();

  const { data: assessmentsData = {} } = useGetClassroomAssessmentsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !selectedClassroomId }
  );

  const [revertQuizPost, { isLoading: isReverting }] = useRevertQuizPostMutation();

  // Only show the classroom that matches the quiz level
  const classrooms = useMemo(() => {
    const classMap = new Map();
    teacherSubjectsData.forEach((item) => {
      if (
        !classMap.has(item.classroom_id) &&
        item.level?.toLowerCase() === quiz?.level?.toLowerCase()
      ) {
        classMap.set(item.classroom_id, {
          id: item.classroom_id,
          name: item.classroom,
          level: item.level,
        });
      }
    });
    return Array.from(classMap.values());
  }, [teacherSubjectsData, quiz?.level]);

  // Only show the subject that matches the quiz subject name
  const subjects = useMemo(() => {
    if (!selectedClassroomId) return [];
    return teacherSubjectsData
      .filter(
        (item) =>
          item.classroom_id === parseInt(selectedClassroomId) &&
          item.name?.toLowerCase() === quiz?.subject?.toLowerCase()
      )
      .map((item) => ({ id: item.id, name: item.name }));
  }, [teacherSubjectsData, selectedClassroomId, quiz?.subject]);

  const assessments = assessmentsData.assessments || [];
  const selectedAssessment = assessments.find((a) => a.id === parseInt(selectedAssessmentId));

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setError(null);
    setReverted(false);
    setSelectedAssessmentId("");
  }, [open]);

  // Auto-preselect classroom
  useEffect(() => {
    if (!open || !classrooms.length) return;
    if (selectedClassroomId) return;
    const match = classrooms.find(
      (c) => c.level?.toLowerCase() === quiz?.level?.toLowerCase()
    );
    if (match) setSelectedClassroomId(match.id);
  }, [open, classrooms, quiz?.level]);

  // Auto-preselect subject
  useEffect(() => {
    if (!selectedClassroomId || !subjects.length) return;
    if (selectedSubjectId) return;
    const match = subjects.find(
      (s) => s.name?.toLowerCase() === quiz?.subject?.toLowerCase()
    );
    if (match) setSelectedSubjectId(match.id);
  }, [selectedClassroomId, subjects, quiz?.subject]);

  const handleClassroomChange = (e) => {
    setSelectedClassroomId(e.target.value);
    setSelectedSubjectId("");
    setSelectedAssessmentId("");
  };

  const handleRevert = async () => {
    setError(null);
    try {
      await revertQuizPost({
        quiz_id: quiz?.id,
        subject_id: parseInt(selectedSubjectId),
        classroom_id: parseInt(selectedClassroomId),
        assessment_id: parseInt(selectedAssessmentId),
      }).unwrap();
      setReverted(true);
    } catch (e) {
      setError(e?.data?.message || "No posted records found for this quiz under the selected assessment.");
    }
  };

  const isValid = selectedClassroomId && selectedSubjectId && selectedAssessmentId;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <UndoIcon color="warning" />
          Revert Quiz Posting
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {reverted ? (
          <Box textAlign="center" py={4}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#2196F3", mb: 2 }} />
            <Typography variant="h6" fontWeight={700}>
              Posting reverted successfully
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Score entries from this quiz have been removed from{" "}
              <b>{selectedAssessment?.name}</b> and affected records recalculated.
            </Typography>
          </Box>
        ) : (
          <Stack gap={2.5}>
            <Alert severity="warning" sx={{ borderRadius: "10px" }}>
              This will remove the raw score entries that were posted from this quiz. If a student
              has other sub-score sheets for the selected assessment those are preserved.
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Classroom</InputLabel>
              <Select
                value={selectedClassroomId}
                onChange={handleClassroomChange}
                label="Classroom"
              >
                {classrooms.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                    {c.level && (
                      <Chip label={c.level} size="small" sx={{ ml: 1 }} variant="outlined" />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedClassroomId}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubjectId}
                onChange={(e) => {
                  setSelectedSubjectId(e.target.value);
                  setSelectedAssessmentId("");
                }}
                label="Subject"
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!selectedSubjectId}>
              <InputLabel>Assessment</InputLabel>
              <Select
                value={selectedAssessmentId}
                onChange={(e) => setSelectedAssessmentId(e.target.value)}
                label="Assessment"
              >
                {assessments.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                    <Chip
                      label={`/${a.obtainable_score}`}
                      size="small"
                      sx={{ ml: 1 }}
                      variant="outlined"
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {error && (
              <Alert severity="error" sx={{ borderRadius: "10px" }}>
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={isReverting}>
          {reverted ? "Close" : "Cancel"}
        </Button>
        {!reverted && (
          <Button
            variant="contained"
            color="warning"
            onClick={handleRevert}
            disabled={!isValid || isReverting}
            startIcon={isReverting ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
          >
            {isReverting ? "Reverting…" : "Revert"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RevertPostingModal;
